import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { cdnKeys } from '@teetsh/app/src/containers/CarnetsDeNotes/queries/keys';
import { useLimitReached } from '@teetsh/app/src/components/LimitReached/useLimitReached';
import { useTranslation } from 'react-i18next';

function duplicateEvaluation({
  schoolyearId,
  schoolId,
  cdnId,
  evaluationId,
  destCarnetId,
}: {
  schoolyearId: string;
  schoolId: string;
  cdnId: string;
  evaluationId: string;
  destCarnetId?: string;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/carnets-de-notes/${cdnId}/evaluations/${evaluationId}/duplicate`;
  const method = 'POST';
  if (destCarnetId) {
    return request(requestURL, {
      method,
      body: { destCarnetId },
    });
  }
  return request(requestURL, {
    method,
  });
}

export function useDuplicateEvaluation(cdnId: string) {
  const { notifyError } = useNotification();
  const { openLimitModal } = useLimitReached();
  const { schoolyear, school } = useSchoolSchoolyear();
  const { t } = useTranslation(['carnet']);
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: ({
      evaluationId,
      destCarnetId,
    }: {
      evaluationId: string;
      destCarnetId?: string;
    }) =>
      duplicateEvaluation({
        schoolId: school.id,
        schoolyearId: schoolyear.id,
        cdnId,
        evaluationId,
        destCarnetId,
      }),

    onSuccess: d => {
      queryClient.invalidateQueries({
        queryKey: cdnKeys.cdnEvaluations(schoolyear.id, school.id, cdnId),
      });
      queryClient.invalidateQueries({
        queryKey: cdnKeys.cdnLimit(schoolyear.id, school.id, cdnId),
      });
      return d.id;
    },
    onError: err => {
      queryClient.invalidateQueries({
        queryKey: cdnKeys.cdnEvaluations(schoolyear.id, school.id, cdnId),
      });
      if (err?.response?.status === 403) {
        openLimitModal({
          requiredPlan: (err?.response?.data as any)?.requiredPlan,
          limit: (err?.response?.data as any)?.limit,
        });
        return;
      }
      notifyError({
        title: t('carnet:messages.evaluation.createError'),
      });
    },
  });
  return mutateAsync;
}
